import { NgTemplateOutlet } from '@angular/common'
import { Component, computed, EventEmitter, input, OnInit, Output } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router'
import { AppMode } from '@ftr/contracts/api/user'
import { Uuid } from '@ftr/contracts/type/shared'
import {
  AnchorComponent,
  BreadcrumbModule,
  BreadcrumbService,
  ButtonColor,
  ButtonComponent,
  IconLegacyComponent,
} from '@ftr/foundation'
import { AppPaths, CourtSystemPaths } from '@ftr/routing-paths'
import { AccountDropdownComponent } from '@ftr/ui-core'
import { RequestCartSidePanelToggleComponent, RequestTypeUrlValue } from '@ftr/ui-request'
import { TopBarSearchComponent } from '@ftr/ui-search'
import { UserState } from '@ftr/ui-user'
import { Store } from '@ngxs/store'
import { filter, map, tap } from 'rxjs'

@Component({
  selector: 'ftr-top-bar-display',
  templateUrl: './top-bar-display.component.html',
  styleUrls: ['./top-bar-display.component.css'],
  imports: [
    AccountDropdownComponent,
    IconLegacyComponent,
    BreadcrumbModule,
    TopBarSearchComponent,
    ButtonComponent,
    NgTemplateOutlet,
    RouterLink,
    AnchorComponent,
    RequestCartSidePanelToggleComponent,
  ],
})
export class TopBarDisplayComponent implements OnInit {
  @Output() sideBarToggled = new EventEmitter<void>()

  currentCourtSystemId = input<Uuid | undefined>()
  currentRequestType = input<RequestTypeUrlValue | undefined>()
  mode = input<AppMode>('courts')
  showingBreadcrumbs = false

  buttonColor: ButtonColor = 'grey'

  protected readonly loginPath = `/${AppPaths.Login}`
  protected readonly signupPath = '/signup'

  protected readonly isLoggedIn = toSignal(this.store.select(UserState.isLoggedIn), { initialValue: false })
  protected routeUrlSignal = computed(() => this.router.url)

  ngOnInit(): void {
    this.showingBreadcrumbs = this.breadcrumbService.isShowingBreadcrumbs(this.activatedRoute)
  }

  protected newRouteUrlSignal = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(event => (event instanceof NavigationEnd ? event.url : '')),
      tap(() => (this.showingBreadcrumbs = this.breadcrumbService.isShowingBreadcrumbs(this.activatedRoute))),
    ),
    { initialValue: '' },
  )

  isSearchingSignal = computed(() => {
    const routeUrl = this.routeUrlSignal()
    const newRouteUrl = this.newRouteUrlSignal()
    const searchUrl = [`/${AppPaths.CourtSystem}`, this.currentCourtSystemId(), CourtSystemPaths.Search].join('/')
    return newRouteUrl.startsWith(searchUrl) || (routeUrl.startsWith(searchUrl) && !newRouteUrl)
  })

  // TODO DI-3132: Strengthen / move logic to show cart button
  readonly shouldShowCartButton = computed<boolean>(() => {
    const isPublicApp = this.mode() === 'public'
    const requestType = this.currentRequestType()
    const routeUrl = this.routeUrlSignal()
    const newRouteUrl = this.newRouteUrlSignal()

    const validRoutes = [`/${AppPaths.Orders}`, `/${AppPaths.Access}`]

    const isValidCartButtonRoute =
      requestType === 'recordings' ||
      validRoutes.some(route => newRouteUrl.startsWith(route)) ||
      validRoutes.some(route => routeUrl.startsWith(route) && !newRouteUrl)

    return Boolean(isPublicApp && isValidCartButtonRoute)
  })

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly store: Store,
  ) {}

  toggleSideBar(): void {
    this.sideBarToggled.emit()
  }
}
